import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { UserX } from 'lucide-react';

interface BlockedUser {
  id: string;
  naam: string | null;
  avatar_url: string | null;
}

export function BlockedUsersSection() {
  const { profile } = useProfile();
  const [blocked, setBlocked] = useState<BlockedUser[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBlocked = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('chat_blocks')
      .select('blocked_id, profiles!chat_blocks_blocked_id_fkey(id, naam, avatar_url)')
      .eq('blocker_id', profile.id);

    if (error) {
      console.error('Failed to load blocked users:', error);
      setBlocked([]);
    } else {
      setBlocked(
        (data ?? [])
          .map((r: any) => r.profiles)
          .filter((p: BlockedUser | null): p is BlockedUser => !!p),
      );
    }
    setLoading(false);
  }, [profile?.id]);

  useEffect(() => {
    fetchBlocked();
  }, [fetchBlocked]);

  const unblock = async (blockedId: string) => {
    if (!profile?.id) return;
    const prev = blocked;
    setBlocked((b) => b.filter((u) => u.id !== blockedId));
    const { error } = await supabase
      .from('chat_blocks')
      .delete()
      .eq('blocker_id', profile.id)
      .eq('blocked_id', blockedId);
    if (error) {
      console.error('Failed to unblock user:', error);
      setBlocked(prev);
    }
  };

  if (loading || blocked.length === 0) return null;

  return (
    <div className="mt-8 pt-6 border-t border-border">
      <h4 className="text-sm font-medium mb-3 text-center">
        Geblokkeerde gebruikers
      </h4>
      <ul className="space-y-2 max-w-md mx-auto">
        {blocked.map((u) => (
          <li
            key={u.id}
            className="flex items-center gap-3 p-2 rounded-md bg-muted/50"
          >
            <Avatar className="h-8 w-8">
              {u.avatar_url && <AvatarImage src={u.avatar_url} alt={u.naam ?? ''} />}
              <AvatarFallback>
                {(u.naam ?? 'U').slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="flex-1 text-sm">{u.naam ?? 'Onbekende gebruiker'}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => unblock(u.id)}
              className="text-muted-foreground hover:text-foreground"
            >
              <UserX className="mr-1 h-4 w-4" />
              Deblokkeren
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
